import { ErroDeApi, ErroDeRede } from '@/lib/erros';
import type { ProblemDetails } from '@/types/api';

/**
 * Cliente HTTP da aplicacao.
 *
 * Especificacao V3.1, item 12.1 e item 12.4 (da V3):
 * - o access token vive em memoria e e injetado por um provedor, nunca lido de
 *   localStorage nem de sessionStorage;
 * - toda resposta de erro e convertida em ErroDeApi preservando o
 *   ProblemDetails, inclusive o correlationId.
 *
 * O refresh token NAO vai em cookie: o backend o devolve e o recebe no corpo
 * (ver AutenticacaoEndpoints.RespostaDeLogin). O `credentials: 'include'` abaixo
 * continua aqui porque nao custa nada e e o unico ponto que mudaria se essa
 * decisao for revista; hoje nenhum cookie de sessao e emitido.
 *
 * O interceptor de 401 e instalado pelo provedor de sessao, em
 * `features/autenticacao`. Este modulo nao conhece o fluxo de renovacao: so sabe
 * que ha um gancho a chamar.
 */

const CABECALHO_CORRELACAO = 'X-Correlation-Id';

type ProvedorDeToken = () => string | null;
type TratadorDeNaoAutorizado = (repetir: () => Promise<Response>) => Promise<Response | null>;

let obterToken: ProvedorDeToken = () => null;
let tratarNaoAutorizado: TratadorDeNaoAutorizado | null = null;

/** Registra de onde o access token em memoria deve ser lido. */
export function definirProvedorDeToken(provedor: ProvedorDeToken): void {
  obterToken = provedor;
}

/**
 * Registra o que fazer diante de 401. Preenchido na Fase 3 pelo fluxo de
 * refresh. Enquanto nao houver autenticacao, 401 sobe como ErroDeApi.
 */
export function definirTratadorDeNaoAutorizado(tratador: TratadorDeNaoAutorizado | null): void {
  tratarNaoAutorizado = tratador;
}

export interface OpcoesDeRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  corpo?: unknown;
  sinal?: AbortSignal;
  cabecalhos?: Record<string, string>;
  /**
   * Desliga o interceptor de 401 para esta requisicao.
   *
   * Obrigatorio nos proprios endpoints de autenticacao. Sem isso, um login com
   * senha errada responderia 401 e o interceptor tentaria renovar a sessao para
   * repetir o login — gastando uma rotacao do refresh token, ou, no caminho em que
   * a renovacao tambem falha, derrubando a sessao de quem apenas errou a senha ao
   * trocar de conta.
   */
  semRenovacao?: boolean;
}

/** Executa a requisicao e desserializa a resposta. */
export async function requisitar<T>(caminho: string, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
  const { metodo = 'GET', corpo, sinal, cabecalhos = {}, semRenovacao = false } = opcoes;

  const executar = async (): Promise<Response> => {
    const cabecalhosFinais: Record<string, string> = {
      Accept: 'application/json',
      ...cabecalhos,
    };

    if (corpo !== undefined) {
      cabecalhosFinais['Content-Type'] = 'application/json';
    }

    const token = obterToken();

    if (token !== null) {
      cabecalhosFinais['Authorization'] = `Bearer ${token}`;
    }

    const requisicao: RequestInit = {
      method: metodo,
      headers: cabecalhosFinais,
      // Necessario para o cookie HttpOnly do refresh token.
      credentials: 'include',
    };

    if (corpo !== undefined) {
      requisicao.body = JSON.stringify(corpo);
    }

    if (sinal !== undefined) {
      requisicao.signal = sinal;
    }

    return fetch(caminho, requisicao);
  };

  let resposta: Response;

  try {
    resposta = await executar();
  } catch (causa) {
    throw new ErroDeRede(causa);
  }

  if (resposta.status === 401 && tratarNaoAutorizado !== null && !semRenovacao) {
    const novaResposta = await tratarNaoAutorizado(executar);

    if (novaResposta !== null) {
      resposta = novaResposta;
    }
  }

  if (!resposta.ok) {
    throw new ErroDeApi(resposta.status, await lerProblema(resposta));
  }

  return lerCorpo<T>(resposta);
}

/**
 * Converte a resposta de erro em ProblemDetails.
 *
 * Nem todo erro vem do nosso pipeline: um proxy ou um gateway pode responder
 * HTML. Nesse caso montamos um ProblemDetails minimo em vez de estourar ao
 * tentar interpretar como JSON.
 */
async function lerProblema(resposta: Response): Promise<ProblemDetails> {
  const correlationId = resposta.headers.get(CABECALHO_CORRELACAO);

  const base: ProblemDetails = {
    status: resposta.status,
    ...(correlationId !== null ? { correlationId } : {}),
  };

  const tipoDeConteudo = resposta.headers.get('Content-Type') ?? '';

  if (!tipoDeConteudo.includes('json')) {
    return {
      ...base,
      title: 'Resposta inesperada do servidor',
      detail: `O servidor respondeu ${String(resposta.status)} sem detalhe interpretavel.`,
    };
  }

  try {
    const corpo = (await resposta.json()) as ProblemDetails;

    return { ...base, ...corpo };
  } catch {
    return {
      ...base,
      title: 'Resposta inesperada do servidor',
      detail: 'Nao conseguimos interpretar a resposta de erro.',
    };
  }
}

async function lerCorpo<T>(resposta: Response): Promise<T> {
  if (resposta.status === 204) {
    return undefined as T;
  }

  const tipoDeConteudo = resposta.headers.get('Content-Type') ?? '';

  if (!tipoDeConteudo.includes('json')) {
    return undefined as T;
  }

  return (await resposta.json()) as T;
}
